import { useState, useEffect } from "react";
import { Sidebar } from "primereact/sidebar";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";

import type { ProgressReportFilterOptions, ProgressReportFilters } from "@/api/progressReport";
import {
  PROGRESS_STATUS_OPTIONS,
  PROGRESS_UNENROLLED_OPTIONS,
  PROGRESS_ARCHIVED_OPTIONS,
  getProgressLevelOptions,
} from "@/constants/assignmentsReport.constants";
import type { CourseData } from "@/api/reports";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LevelsData {
  courses?: CourseData[];
  coursesByRank?: Record<string, CourseData>;
}

interface ProgressReportFiltersProps {
  visible: boolean;
  onHide: () => void;
  filterOptions: ProgressReportFilterOptions | null;
  loadingOptions: boolean;
  levelsData: LevelsData;
  loadingLevels: boolean;
  pathOptions: string[];
  categoryOptions: { label: string; value: string }[];
  loadingCollections: boolean;
  currentFilters: ProgressReportFilters;
  onApply: (filters: ProgressReportFilters) => void;
  onClear: () => void;
}

// ---------------------------------------------------------------------------
// FilterField — reusable labelled MultiSelect row
// ---------------------------------------------------------------------------

interface FilterFieldProps {
  placeholder: string;
  options: string[] | undefined;
  value: string[];
  onChange: (value: string[]) => void;
  loading: boolean;
}

function FilterField({ placeholder, options, value, onChange, loading }: FilterFieldProps) {
  return loading ? (
    <Skeleton width="100%" height="2.5rem" />
  ) : (
    <MultiSelect
      options={(options ?? []).map(v => ({ label: v, value: v }))}
      value={value}
      onChange={e => onChange(e.value as string[])}
      placeholder={placeholder}
      display="chip"
      maxSelectedLabels={2}
      filter
      className="w-full"
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProgressReportFilters({
  visible,
  onHide,
  filterOptions,
  loadingOptions,
  levelsData,
  loadingLevels,
  pathOptions,
  categoryOptions,
  loadingCollections,
  currentFilters,
  onApply,
  onClear,
}: ProgressReportFiltersProps) {
  const [draft, setDraft] = useState<ProgressReportFilters>(currentFilters);

  // Re-sync draft from latest persisted filters every time the sidebar opens
  useEffect(() => {
    if (visible) setDraft(currentFilters);
  }, [visible]);

  const setField = (field: keyof ProgressReportFilters, value: string[]) =>
    setDraft(prev => ({ ...prev, [field]: { value } }));

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      className="w-lg"
      aria-label="Advanced filters"
    >
      <div className="flex flex-col space-y-10 pb-16">
        {/* Category */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Category</h3>
          {loadingCollections ? (
            <Skeleton width="100%" height="2.5rem" />
          ) : (
            <MultiSelect
              options={categoryOptions}
              value={draft.category?.value ?? []}
              onChange={e => setField("category", e.value as string[])}
              placeholder="Select categories"
              display="chip"
              maxSelectedLabels={2}
              className="w-full"
            />
          )}
        </section>

        {/* Status */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Status</h3>
          <MultiSelect
            options={PROGRESS_STATUS_OPTIONS}
            value={draft.status?.value ?? []}
            onChange={e => setField("status", e.value as string[])}
            placeholder="Select status"
            display="chip"
            maxSelectedLabels={2}
            filter
            className="w-full"
          />
        </section>

        {/* Level */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Level</h3>
          {loadingLevels ? (
            <Skeleton width="100%" height="2.5rem" />
          ) : (
            <MultiSelect
              options={getProgressLevelOptions(levelsData)}
              value={draft.level?.value ?? []}
              onChange={e => setField("level", e.value as string[])}
              placeholder="Select levels"
              display="chip"
              maxSelectedLabels={2}
              className="w-full"
            />
          )}
        </section>

        {/* User Information */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">User Information</h3>
          <FilterField
            placeholder="Select job roles"
            options={filterOptions?.jobRole}
            value={draft.jobRole?.value ?? []}
            onChange={v => setField("jobRole", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select titles"
            options={filterOptions?.title}
            value={draft.title?.value ?? []}
            onChange={v => setField("title", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select managers"
            options={filterOptions?.manager}
            value={draft.manager?.value ?? []}
            onChange={v => setField("manager", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select manager emails"
            options={filterOptions?.managersEmail}
            value={draft.managersEmail?.value ?? []}
            onChange={v => setField("managersEmail", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select employee numbers"
            options={filterOptions?.employeeNumber}
            value={draft.employeeNumber?.value ?? []}
            onChange={v => setField("employeeNumber", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select security champion"
            options={filterOptions?.securityChampion}
            value={draft.securityChampion?.value ?? []}
            onChange={v => setField("securityChampion", v)}
            loading={loadingOptions}
          />
        </section>

        {/* Organization */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Organization</h3>
          <FilterField
            placeholder="Select departments"
            options={filterOptions?.department}
            value={draft.department?.value ?? []}
            onChange={v => setField("department", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select business units"
            options={filterOptions?.businessUnit}
            value={draft.businessUnit?.value ?? []}
            onChange={v => setField("businessUnit", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select teams"
            options={filterOptions?.team}
            value={draft.team?.value ?? []}
            onChange={v => setField("team", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select companies"
            options={filterOptions?.company}
            value={draft.company?.value ?? []}
            onChange={v => setField("company", v)}
            loading={loadingOptions}
          />
          <FilterField
            placeholder="Select countries"
            options={filterOptions?.country}
            value={draft.country?.value ?? []}
            onChange={v => setField("country", v)}
            loading={loadingOptions}
          />
        </section>

        {/* Paths */}
        <section className="flex flex-col space-y-4">
          <h3 className="atlas-h5">Paths</h3>
          <FilterField
            placeholder="Select paths"
            options={pathOptions}
            value={draft.path?.value ?? []}
            onChange={v => setField("path", v)}
            loading={loadingCollections}
          />
          <MultiSelect
            options={PROGRESS_UNENROLLED_OPTIONS}
            value={draft.unenrolled?.value ?? []}
            onChange={e => setField("unenrolled", e.value as string[])}
            placeholder="Select unenrolled options"
            display="chip"
            maxSelectedLabels={2}
            className="w-full"
          />
          <MultiSelect
            options={PROGRESS_ARCHIVED_OPTIONS}
            value={draft.archived?.value ?? []}
            onChange={e => setField("archived", e.value as string[])}
            placeholder="Select archived options"
            display="chip"
            maxSelectedLabels={2}
            className="w-full"
          />
        </section>

        <div className="flex gap-2 pt-4 border-t">
          <Button label="Clear All" severity="secondary" onClick={onClear} className="flex-1" />
          <Button label="Cancel" severity="secondary" onClick={onHide} />
          <Button label="Apply" onClick={() => onApply(draft)} className="flex-1" />
        </div>
      </div>
    </Sidebar>
  );
}
